import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db, loginAdmin } from "../../Firebase";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import Avatar from "@mui/material/Avatar";
import { CircleLoader } from "react-spinners";
const selectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: "8px",
    border: "none",
    outline: "none",
    boxShadow: "none",
    fontSize: "14px",
    fontFamily: "inherit",
    cursor: "pointer",
    minHeight: "44px",
  }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: "14px", fontFamily: "inherit" }),
  option: (base, { isFocused }) => ({
    ...base,
    backgroundColor: isFocused ? "#f3f4f6" : "white",
    color: "#1f2937",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }),
  singleValue: (base) => ({ ...base, color: "#1f2937", fontSize: "14px" }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "14px" }),
};

const UserOption = ({ innerProps, data, isFocused }) => (
  <div
    {...innerProps}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 12px",
      backgroundColor: isFocused ? "#f3f4f6" : "white",
      cursor: "pointer",
    }}
  >
    <Avatar
      src={data.imageUrl || ""}
      alt={data.label}
      sx={{ width: 32, height: 32, flexShrink: 0 }}
    />
    <div>
      <div style={{ fontWeight: 600, fontSize: "14px", color: "#1f2937" }}>{data.label}</div>
      {data.email && <div style={{ fontSize: "12px", color: "#6b7280" }}>{data.email}</div>}
    </div>
  </div>
);

// Custom single value with avatar
const UserSingleValue = ({ data }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <Avatar src={data.imageUrl || ""} alt={data.label} sx={{ width: 24, height: 24 }} />
    <span style={{ fontSize: "14px", color: "#1f2937" }}>{data.label}</span>
  </div>
);

const PairOption = ({ innerProps, data, isFocused }) => (
  <div
    {...innerProps}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 12px",
      backgroundColor: isFocused ? "#f3f4f6" : "white",
      cursor: "pointer",
    }}
  >
    <div style={{ display: "flex", alignItems: "center" }}>
      <Avatar src={data.user1Image} alt={data.user1Name} sx={{ width: 26, height: 26 }} />
      <Avatar
        src={data.user2Image}
        alt={data.user2Name}
        sx={{ width: 26, height: 26, marginLeft: "-8px", border: "2px solid white" }}
      />
    </div>
    <div>
      <div style={{ fontWeight: 600, fontSize: "14px", color: "#1f2937" }}>
        {data.user1Name} &amp; {data.user2Name}
      </div>
      <div style={{ fontSize: "11px", color: "#6b7280" }}>Pair ID: {data.value?.slice(0, 8)}...</div>
    </div>
  </div>
);

const PairSingleValue = ({ data }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <div style={{ display: "flex", alignItems: "center" }}>
      <Avatar src={data.user1Image} alt={data.user1Name} sx={{ width: 22, height: 22 }} />
      <Avatar
        src={data.user2Image}
        alt={data.user2Name}
        sx={{ width: 22, height: 22, marginLeft: "-6px", border: "1px solid white" }}
      />
    </div>
    <span style={{ fontSize: "14px", color: "#1f2937", fontWeight: 600 }}>
      {data.user1Name} &amp; {data.user2Name}
    </span>
  </div>
);

// ─── Date helpers ──────────────────────────────────────────────────────────────
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfWeek = (d) => {
  const day = d.getDay(); // 0 = Sun
  const diff = d.getDate() - day;
  return new Date(d.getFullYear(), d.getMonth(), diff);
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const FILTER_OPTIONS = [
  { key: "all", label: "All" },
  { key: "daily", label: "Today" },
  { key: "weekly", label: "This Week" },
  { key: "monthly", label: "This Month" },
];

const Matches = () => {
  const [users, setUsers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [doubleDates, setDoubleDates] = useState([]);

  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [loadingPairs, setLoadingPairs] = useState(true);
  const [loadingDoubleDates, setLoadingDoubleDates] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [submittingPair, setSubmittingPair] = useState(false);
  const [submittingDoubleDate, setSubmittingDoubleDate] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [deletingPairId, setDeletingPairId] = useState(null);
  const [deletingDoubleDateId, setDeletingDoubleDateId] = useState(null);

  const [activeTab, setActiveTab] = useState("matches");
  const [activeFilter, setActiveFilter] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [currentPairPage, setCurrentPairPage] = useState(1);
  const [currentDoubleDatePage, setCurrentDoubleDatePage] = useState(1);
  const itemsPerPage = 100;

  const [user1, setUser1] = useState(null);
  const [user2, setUser2] = useState(null);
  const [pairUser1, setPairUser1] = useState(null);
  const [pairUser2, setPairUser2] = useState(null);

  const [selectPair1, setSelectPair1] = useState(null);
  const [selectPair2, setSelectPair2] = useState(null);

  // ── Derived counts ───────────────────────────────────────────────────────────
  const now = new Date();
  const dailyCount = matches.filter((m) => m._matchedAt >= startOfDay(now)).length;
  const weeklyCount = matches.filter((m) => m._matchedAt >= startOfWeek(now)).length;
  const monthlyCount = matches.filter((m) => m._matchedAt >= startOfMonth(now)).length;

  const filteredMatches = matches.filter((m) => {
    if (activeFilter === "daily") return m._matchedAt >= startOfDay(now);
    if (activeFilter === "weekly") return m._matchedAt >= startOfWeek(now);
    if (activeFilter === "monthly") return m._matchedAt >= startOfMonth(now);
    return true;
  });

  const totalPages = Math.ceil(filteredMatches.length / itemsPerPage) || 1;
  const paginatedMatches = filteredMatches.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPairPages = Math.ceil(pairs.length / itemsPerPage) || 1;
  const paginatedPairs = pairs.slice(
    (currentPairPage - 1) * itemsPerPage,
    currentPairPage * itemsPerPage
  );

  const totalDoubleDatePages = Math.ceil(doubleDates.length / itemsPerPage) || 1;
  const paginatedDoubleDates = doubleDates.slice(
    (currentDoubleDatePage - 1) * itemsPerPage,
    currentDoubleDatePage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  // ── Fetch all Vortex users ───────────────────────────────────────────────────
  const fetchUsers = async () => {
    try {
      await loginAdmin();
      const snap = await getDocs(collection(db, "users"));
      const list = snap.docs
        .map((d) => ({
          value: d.id,
          label: d.data().name || "Unnamed",
          email: d.data().email || "",
          imageUrl: d.data().imageUrl || "",
          rawData: d.data(),
        }))
        .filter((u) => !!u.label.trim() && u.label !== "Unnamed");
      setUsers(list);
      return list;
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("Failed to load users");
      return [];
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchMatches = async (loadedUsers = users) => {
    setLoadingMatches(true);
    try {
      await loginAdmin();
      const snap = await getDocs(collection(db, "likes"));

      const list = snap.docs
        .map((d) => {
          const data = d.data();
          const participants = data.participants || {};
          const keys = Object.keys(participants);
          const isMatch =
            keys.length >= 2 && participants[keys[0]] === true && participants[keys[1]] === true;

          if (!isMatch) return null;

          const matchedAt = data.matchedAt?.toDate
            ? data.matchedAt.toDate()
            : data.timestamp?.toDate
              ? data.timestamp.toDate()
              : new Date(0);

          const user1Id = data.isSender || keys[0] || "";
          const user2Id = data.isReceiver || keys[1] || "";
          const u1 = loadedUsers.find((u) => u.value === user1Id);
          const u2 = loadedUsers.find((u) => u.value === user2Id);

          return {
            id: d.id,
            _matchedAt: matchedAt,
            matchedAt: matchedAt.getTime() === 0 ? "N/A" : matchedAt.toLocaleString(),
            user1Id,
            user2Id,
            user1Name: data.isSenderName || data.user1Name || u1?.label || "Unknown",
            user2Name: data.isReceiverName || data.user2Name || u2?.label || "Unknown",
            user1Image: data.isSenderImage || data.user1Image || u1?.imageUrl || "",
            user2Image: data.isReceiverImage || data.user2Image || u2?.imageUrl || "",
            status: data.status || "active",
            createdByAdmin: !!data.createdByAdmin,
            chatroomId: data.chatroomId || null,
          };
        })
        .filter(Boolean)
        .sort((a, b) => b._matchedAt - a._matchedAt);

      setMatches(list);
    } catch (err) {
      console.error("Error fetching matches:", err);
      toast.error("Failed to load matches");
    } finally {
      setLoadingMatches(false);
    }
  };

  const fetchPairs = async (loadedUsers = users) => {
    setLoadingPairs(true);
    try {
      await loginAdmin();
      const snap = await getDocs(collection(db, "pairs"));

      const list = snap.docs
        .map((d) => {
          const data = d.data();
          const u1Data = data.user1 || {};
          const u2Data = data.user2 || {};

          const uid1 = u1Data.uid || u1Data.id || "";
          const uid2 = u2Data.uid || u2Data.id || "";

          const u1 = loadedUsers.find((u) => u.value === uid1);
          const u2 = loadedUsers.find((u) => u.value === uid2);

          const createdAt = data.createdAt?.toDate
            ? data.createdAt.toDate()
            : data.timestamp?.toDate
              ? data.timestamp.toDate()
              : new Date(0);

          return {
            id: d.id,
            value: d.id,
            label: `${u1Data.name || u1?.label || "User 1"} & ${u2Data.name || u2?.label || "User 2"}`,
            _createdAt: createdAt,
            createdAt: createdAt.getTime() === 0 ? "N/A" : createdAt.toLocaleString(),
            user1Id: uid1,
            user2Id: uid2,
            user1Name: u1Data.name || u1?.label || "User 1",
            user2Name: u2Data.name || u2?.label || "User 2",
            user1Image: u1Data.imageUrl || u1?.imageUrl || "",
            user2Image: u2Data.imageUrl || u2?.imageUrl || "",
            user1Email: u1Data.email || u1?.email || "",
            user2Email: u2Data.email || u2?.email || "",
          };
        })
        .sort((a, b) => b._createdAt - a._createdAt);

      setPairs(list);
      return list;
    } catch (err) {
      console.error("Error fetching pairs:", err);
      toast.error("Failed to load pairs");
      return [];
    } finally {
      setLoadingPairs(false);
    }
  };
  const fetchDoubleDates = async (loadedPairs = pairs) => {
    setLoadingDoubleDates(true);
    try {
      await loginAdmin();
      const snap = await getDocs(collection(db, "pairRequests"));

      const list = snap.docs
        .map((d) => {
          const data = d.data();
          const fromPairId = data.fromPairId;
          const toPairId = data.toPairId;
          const status = data.status || "pending";

          const pairA = loadedPairs.find((p) => p.id === fromPairId);
          const pairB = loadedPairs.find((p) => p.id === toPairId);

          const createdAt = data.createdAt?.toDate
            ? data.createdAt.toDate()
            : data.timestamp?.toDate
              ? data.timestamp.toDate()
              : new Date(0);

          return {
            id: d.id,
            fromPairId,
            toPairId,
            status,
            chatId: data.chatId || null,
            _createdAt: createdAt,
            createdAt: createdAt.getTime() === 0 ? "N/A" : createdAt.toLocaleString(),
            pairA,
            pairB,
            pairAName: pairA ? `${pairA.user1Name} & ${pairA.user2Name}` : "Pair 1",
            pairBName: pairB ? `${pairB.user1Name} & ${pairB.user2Name}` : "Pair 2",
          };
        })
        .sort((a, b) => b._createdAt - a._createdAt);

      setDoubleDates(list);
    } catch (err) {
      console.error("Error fetching double dates:", err);
    } finally {
      setLoadingDoubleDates(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const loadedUsers = await fetchUsers();
      const loadedPairs = await fetchPairs(loadedUsers);
      await Promise.all([fetchMatches(loadedUsers), fetchDoubleDates(loadedPairs)]);
    };
    init();
  }, []);

  const handleCreateMatch = async () => {
    if (!user1 || !user2) {
      toast.error("Please select both users");
      return;
    }
    if (user1.value === user2.value) {
      toast.error("Please select two different users");
      return;
    }

    const exists = matches.some(
      (m) =>
        (m.user1Id === user1.value && m.user2Id === user2.value) ||
        (m.user1Id === user2.value && m.user2Id === user1.value)
    );
    if (exists) {
      toast.error("A match between these users already exists");
      return;
    }

    setSubmitting(true);
    try {
      await loginAdmin();
      const now = Timestamp.now();

      const likeRef = doc(collection(db, "likes"));
      const chatRoomRef = doc(collection(db, "chatrooms"));

      await setDoc(doc(db, "matches", likeRef.id), {
        matchId: likeRef.id,
        user1Id: user1.value,
        user2Id: user2.value,
        user1Name: user1.label,
        user2Name: user2.label,
        user1Image: user1.imageUrl || "",
        user2Image: user2.imageUrl || "",
        matchedAt: now,
        status: "active",
        createdByAdmin: true,
        likeId: likeRef.id,
        chatroomId: chatRoomRef.id,
      });

      await setDoc(doc(db, "users", user1.value, "matches", likeRef.id), {
        matchId: likeRef.id,
        uid: user2.value,
        name: user2.label,
        imageUrl: user2.imageUrl || "",
        matchedAt: now,
        status: "active",
      });

      await setDoc(doc(db, "users", user2.value, "matches", likeRef.id), {
        matchId: likeRef.id,
        uid: user1.value,
        name: user1.label,
        imageUrl: user1.imageUrl || "",
        matchedAt: now,
        status: "active",
      });

      await setDoc(likeRef, {
        likeId: likeRef.id,
        isSender: user1.value,
        isReceiver: user2.value,
        isSenderName: user1.label,
        isReceiverName: user2.label,
        isSenderImage: user1.imageUrl || "",
        isReceiverImage: user2.imageUrl || "",
        participants: {
          [user1.value]: true,
          [user2.value]: true,
        },
        matchedAt: now,
      });

      await setDoc(chatRoomRef, {
        chatroomId: chatRoomRef.id,
        participants: {
          [user1.value]: true,
          [user2.value]: true,
        },
        lastMessage: "",
        lastMesgUserId: user1.value,
        isReadSender: true,
        isReadReceiver: false,
        lastMessageTime: now,
        typing: [],
        recording: [],
      });

      toast.success(`✅ Match created: ${user1.label} ↔ ${user2.label}`);
      setUser1(null);
      setUser2(null);
      fetchMatches(users);
    } catch (err) {
      console.error("Error creating match:", err);
      toast.error("Failed to create match");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreatePair = async () => {
    if (!pairUser1 || !pairUser2) {
      toast.error("Please select both users to form a pair");
      return;
    }
    if (pairUser1.value === pairUser2.value) {
      toast.error("Please select two different users");
      return;
    }

    const exists = pairs.some(
      (p) =>
        (p.user1Id === pairUser1.value && p.user2Id === pairUser2.value) ||
        (p.user1Id === pairUser2.value && p.user2Id === pairUser1.value)
    );
    if (exists) {
      toast.error("These two users are already paired in Deuces");
      return;
    }

    setSubmittingPair(true);
    try {
      await loginAdmin();
      const now = Timestamp.now();

      await addDoc(collection(db, "pairs"), {
        user1: {
          uid: pairUser1.value,
          name: pairUser1.label,
          email: pairUser1.email || "",
          imageUrl: pairUser1.imageUrl || "",
        },
        user2: {
          uid: pairUser2.value,
          name: pairUser2.label,
          email: pairUser2.email || "",
          imageUrl: pairUser2.imageUrl || "",
        },
        createdAt: now,
      });

      toast.success(`Deuces Pair created: ${pairUser1.label} 🤝 ${pairUser2.label}`);
      setPairUser1(null);
      setPairUser2(null);
      const updatedPairs = await fetchPairs(users);
      fetchDoubleDates(updatedPairs);
    } catch (err) {
      console.error("Error creating pair:", err);
      toast.error("Failed to create pair");
    } finally {
      setSubmittingPair(false);
    }
  };

  const handleCreateDoubleDate = async () => {
    if (!selectPair1 || !selectPair2) {
      toast.error("Please select two pairs for the double date");
      return;
    }
    if (selectPair1.value === selectPair2.value) {
      toast.error("Please select two different pairs");
      return;
    }

    const exists = doubleDates.some(
      (d) =>
        (d.fromPairId === selectPair1.value && d.toPairId === selectPair2.value) ||
        (d.fromPairId === selectPair2.value && d.toPairId === selectPair1.value)
    );
    if (exists) {
      toast.error("A double date match between these two pairs already exists");
      return;
    }

    setSubmittingDoubleDate(true);
    try {
      await loginAdmin();
      const now = Timestamp.now();

      const pairChatRef = doc(collection(db, "pairChats"));
      const pairRequestRef = doc(collection(db, "pairRequests"));

      await setDoc(pairChatRef, {
        participants: [selectPair1.value, selectPair2.value],
        requestId: pairRequestRef.id,
        createdAt: now,
      });
      await setDoc(pairRequestRef, {
        fromPairId: selectPair1.value,
        toPairId: selectPair2.value,
        status: "accepted",
        chatId: pairChatRef.id,
        createdAt: now,
        createdByAdmin: true,
      });

      toast.success(`4-Person Double Date Match Created: [${selectPair1.label}] ↔ [${selectPair2.label}]`);
      setSelectPair1(null);
      setSelectPair2(null);
      fetchDoubleDates(pairs);
    } catch (err) {
      console.error("Error creating double date:", err);
      toast.error("Failed to create double date match");
    } finally {
      setSubmittingDoubleDate(false);
    }
  };
  const handleDeleteMatch = async (match) => {
    if (!window.confirm(`Delete match between ${match.user1Name} and ${match.user2Name}?`)) return;

    setDeletingId(match.id);
    try {
      await loginAdmin();

      await deleteDoc(doc(db, "likes", match.id));

      // Delete corresponding doc from matches collection
      try {
        await deleteDoc(doc(db, "matches", match.id));
      } catch (_) { }
      try {
        await deleteDoc(doc(db, "users", match.user1Id, "matches", match.id));
      } catch (_) { }
      try {
        await deleteDoc(doc(db, "users", match.user2Id, "matches", match.id));
      } catch (_) { }

      if (match.chatroomId) {
        try {
          await deleteDoc(doc(db, "chatrooms", match.chatroomId));
        } catch (_) { }
      }

      toast.success("Match deleted");
      setMatches((prev) => prev.filter((m) => m.id !== match.id));
    } catch (err) {
      console.error("Error deleting match:", err);
      toast.error("Failed to delete match");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeletePair = async (pair) => {
    if (!window.confirm(`Unpair / Delete Deuces pair between ${pair.user1Name} and ${pair.user2Name}?`))
      return;

    setDeletingPairId(pair.id);
    try {
      await loginAdmin();
      await deleteDoc(doc(db, "pairs", pair.id));
      toast.success("Deuces pair deleted");
      setPairs((prev) => prev.filter((p) => p.id !== pair.id));
    } catch (err) {
      console.error("Error deleting pair:", err);
      toast.error("Failed to delete pair");
    } finally {
      setDeletingPairId(null);
    }
  };

  const handleDeleteDoubleDate = async (dd) => {
    if (!window.confirm(`Delete double date match between ${dd.pairAName} and ${dd.pairBName}?`)) return;

    setDeletingDoubleDateId(dd.id);
    try {
      await loginAdmin();

      await deleteDoc(doc(db, "pairRequests", dd.id));

      if (dd.chatId) {
        try {
          await deleteDoc(doc(db, "pairChats", dd.chatId));
        } catch (_) { }
      }

      try {
        const chatSnap = await getDocs(
          query(
            collection(db, "pairChats"),
            where("participants", "array-contains", dd.fromPairId)
          )
        );
        for (const chatDoc of chatSnap.docs) {
          const participants = chatDoc.data().participants || [];
          if (participants.includes(dd.toPairId)) {
            await deleteDoc(doc(db, "pairChats", chatDoc.id));
          }
        }
      } catch (_) { }

      toast.success("Double date match deleted");
      setDoubleDates((prev) => prev.filter((d) => d.id !== dd.id));
    } catch (err) {
      console.error("Error deleting double date:", err);
      toast.error("Failed to delete double date match");
    } finally {
      setDeletingDoubleDateId(null);
    }
  };

  const acceptedDoubleDatesCount = doubleDates.filter((d) => d.status === "accepted").length;

  return (
    <div
      style={{
        flex: 1,
        minHeight: "100vh",
        backgroundColor: "#374151",
        color: "white",
        padding: "24px 16px",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <ToastContainer />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          marginBottom: "24px",
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "clamp(1.4rem, 4vw, 2.2rem)", fontWeight: 700 }}>
          💘 Vortex Matches &amp; Deuces
        </h1>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "999px",
              backgroundColor: "#1f2937",
              fontSize: "13px",
              color: "#ec4899",
              fontWeight: 600,
              border: "1px solid #ec489933",
            }}
          >
            💘 {matches.length} Solo Match{matches.length !== 1 ? "es" : ""} (2p)
          </span>
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "999px",
              backgroundColor: "#1f2937",
              fontSize: "13px",
              color: "#38bdf8",
              fontWeight: 600,
              border: "1px solid #38bdf833",
            }}
          >
            🎉 {acceptedDoubleDatesCount} Double Date{acceptedDoubleDatesCount !== 1 ? "s" : ""} (4p)
          </span>
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "999px",
              backgroundColor: "#1f2937",
              fontSize: "13px",
              color: "#a855f7",
              fontWeight: 600,
              border: "1px solid #a855f733",
            }}
          >
            👥 {pairs.length} Deuces Team{pairs.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* ── Stats Cards ────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: "16px",
          marginBottom: "28px",
          maxWidth: "1150px",
        }}
      >
        {[
          { label: "Solo Matches (1-on-1)", count: matches.length, icon: "💘", color: "#ec4899", sub: "2 Users per match" },
          { label: "Double Dates (2v2)", count: acceptedDoubleDatesCount, icon: "🎉", color: "#38bdf8", sub: "4 Users (2 pairs)" },
          { label: "Total Deuces Teams", count: pairs.length, icon: "👥", color: "#a855f7", sub: "2-person registered pairs" },
          { label: "Solo Matches Today", count: dailyCount, icon: "☀️", color: "#f59e0b", sub: "Today's 1-on-1 activity" },
          { label: "Solo Matches Week", count: weeklyCount, icon: "📅", color: "#10b981", sub: "This week" },
        ].map(({ label, count, icon, color, sub }) => (
          <div
            key={label}
            style={{
              backgroundColor: "#1f2937",
              borderRadius: "12px",
              padding: "18px",
              borderLeft: `4px solid ${color}`,
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ fontSize: "22px", marginBottom: "8px" }}>{icon}</div>
            <div style={{ fontSize: "28px", fontWeight: 800, color }}>{count}</div>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "#f3f4f6", marginTop: "4px" }}>{label}</div>
            {sub && <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>{sub}</div>}
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "24px",
          borderBottom: "1px solid #4b5563",
          paddingBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => setActiveTab("matches")}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: activeTab === "matches" ? "#ec4899" : "#1f2937",
            color: activeTab === "matches" ? "white" : "#d1d5db",
            fontSize: "14px",
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s",
          }}
        >
          <span>💘 Solo Matches (1-on-1)</span>
          <span
            style={{
              backgroundColor: activeTab === "matches" ? "rgba(255,255,255,0.2)" : "#374151",
              padding: "2px 8px",
              borderRadius: "999px",
              fontSize: "12px",
            }}
          >
            {matches.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab("doubledates")}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: activeTab === "doubledates" ? "#38bdf8" : "#1f2937",
            color: activeTab === "doubledates" ? "#0f172a" : "#d1d5db",
            fontSize: "14px",
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s",
          }}
        >
          <span>Deuces Double Dates (4 Users / 2v2)</span>
          <span
            style={{
              backgroundColor: activeTab === "doubledates" ? "rgba(0,0,0,0.15)" : "#374151",
              padding: "2px 8px",
              borderRadius: "999px",
              fontSize: "12px",
            }}
          >
            {doubleDates.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab("pairs")}
          style={{
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            backgroundColor: activeTab === "pairs" ? "#a855f7" : "#1f2937",
            color: activeTab === "pairs" ? "white" : "#d1d5db",
            fontSize: "14px",
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s",
          }}
        >
          <span>Registered Deuces Pairs (2p Teams)</span>
          <span
            style={{
              backgroundColor: activeTab === "pairs" ? "rgba(255,255,255,0.2)" : "#374151",
              padding: "2px 8px",
              borderRadius: "999px",
              fontSize: "12px",
            }}
          >
            {pairs.length}
          </span>
        </button>
      </div>

      {activeTab === "matches" && (
        <>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "24px",
              flexWrap: "wrap",
            }}
          >
            {FILTER_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                style={{
                  padding: "8px 20px",
                  borderRadius: "999px",
                  border: activeFilter === key ? "none" : "1px solid #4b5563",
                  backgroundColor: activeFilter === key ? "#ec4899" : "transparent",
                  color: activeFilter === key ? "white" : "#9ca3af",
                  fontSize: "13px",
                  fontWeight: activeFilter === key ? 700 : 500,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "all 0.15s",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div
            style={{
              backgroundColor: "#1f2937",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "32px",
              maxWidth: "860px",
            }}
          >
            <h2 style={{ margin: "0 0 20px 0", fontSize: "17px", fontWeight: 700, color: "white" }}>
              🔗 Pair Two Users (1-on-1 Solo Match)
            </h2>

            {loadingUsers ? (
              <div style={{ display: "flex", alignItems: "center", gap: "12px", color: "#9ca3af" }}>
                <CircleLoader color="#ec4899" size={24} />
                <span>Loading users…</span>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: "20px",
                    marginBottom: "24px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <label style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 500 }}>
                      👤 User 1
                    </label>
                    <Select
                      options={users.filter((u) => u.value !== user2?.value)}
                      value={user1}
                      onChange={setUser1}
                      placeholder="Search user…"
                      styles={selectStyles}
                      components={{ Option: UserOption, SingleValue: UserSingleValue }}
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "28px",
                      alignSelf: "flex-end",
                      paddingBottom: "2px",
                    }}
                  >
                    ❤️
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <label style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 500 }}>
                      👤 User 2
                    </label>
                    <Select
                      options={users.filter((u) => u.value !== user1?.value)}
                      value={user2}
                      onChange={setUser2}
                      placeholder="Search user…"
                      styles={selectStyles}
                      components={{ Option: UserOption, SingleValue: UserSingleValue }}
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>

                {user1 && user2 && (
                  <div
                    style={{
                      backgroundColor: "#374151",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Avatar src={user1.imageUrl} alt={user1.label} sx={{ width: 44, height: 44 }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "14px" }}>{user1.label}</div>
                        <div style={{ fontSize: "12px", color: "#9ca3af" }}>{user1.email}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: "24px", padding: "0 8px" }}>💞</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Avatar src={user2.imageUrl} alt={user2.label} sx={{ width: 44, height: 44 }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "14px" }}>{user2.label}</div>
                        <div style={{ fontSize: "12px", color: "#9ca3af" }}>{user2.email}</div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleCreateMatch}
                  disabled={submitting || !user1 || !user2}
                  style={{
                    padding: "12px 32px",
                    backgroundColor: submitting || !user1 || !user2 ? "#4b5563" : "#ec4899",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: 700,
                    cursor: submitting || !user1 || !user2 ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "background-color 0.2s",
                  }}
                >
                  {submitting ? (
                    <>
                      <CircleLoader color="white" size={16} />
                      Creating…
                    </>
                  ) : (
                    "💘 Create 1-on-1 Match"
                  )}
                </button>
              </>
            )}
          </div>

          {/* Table */}
          <div style={{ maxWidth: "960px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "14px",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
                🗂️ {FILTER_OPTIONS.find((f) => f.key === activeFilter)?.label} Matches
                <span style={{ fontSize: "14px", fontWeight: 400, color: "#9ca3af", marginLeft: "10px" }}>
                  {filteredMatches.length} match{filteredMatches.length !== 1 ? "es" : ""}
                </span>
              </h2>
              <button
                onClick={() => fetchMatches(users)}
                style={{
                  padding: "7px 16px",
                  borderRadius: "6px",
                  border: "1px solid #4b5563",
                  backgroundColor: "transparent",
                  color: "#d1d5db",
                  fontSize: "13px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontFamily: "inherit",
                }}
              >
                Refresh
              </button>
            </div>

            {loadingMatches ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "180px" }}>
                <CircleLoader color="#ec4899" size={40} />
              </div>
            ) : filteredMatches.length === 0 ? (
              <div
                style={{
                  backgroundColor: "#1f2937",
                  borderRadius: "10px",
                  padding: "40px",
                  textAlign: "center",
                  color: "#6b7280",
                  fontSize: "15px",
                }}
              >
                No matches found.
              </div>
            ) : (
              <div
                style={{
                  overflowX: "auto",
                  backgroundColor: "#1f2937",
                  borderRadius: "12px",
                  border: "1px solid #374151",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #374151", backgroundColor: "#111827", textAlign: "left" }}>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500 }}>User 1</th>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500 }}>User 2</th>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500 }}>Matched At</th>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500, textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMatches.map((match) => (
                      <tr
                        key={match.id}
                        style={{ borderBottom: "1px solid #374151", transition: "background-color 0.2s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#374151")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Avatar src={match.user1Image} alt={match.user1Name} sx={{ width: 36, height: 36 }} />
                            <div>
                              <div style={{ fontWeight: 600 }}>{match.user1Name}</div>
                              <div style={{ fontSize: "11px", color: "#9ca3af" }}>{match.user1Id.slice(0, 10)}...</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Avatar src={match.user2Image} alt={match.user2Name} sx={{ width: 36, height: 36 }} />
                            <div>
                              <div style={{ fontWeight: 600 }}>{match.user2Name}</div>
                              <div style={{ fontSize: "11px", color: "#9ca3af" }}>{match.user2Id.slice(0, 10)}...</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: "#9ca3af" }}>
                          {match.matchedAt}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                          <button
                            onClick={() => handleDeleteMatch(match)}
                            disabled={deletingId === match.id}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#ef4444",
                              fontSize: "13px",
                              fontWeight: 600,
                            }}
                          >
                            {deletingId === match.id ? <CircleLoader size={16} color="#ef4444" /> : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: currentPage === 1 ? "#374151" : "#ec4899",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Previous
                </button>
                <span style={{ color: "#9ca3af", fontSize: "14px" }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: currentPage === totalPages ? "#374151" : "#ec4899",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "doubledates" && (
        <>
          <div
            style={{
              backgroundColor: "#1f2937",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "32px",
              maxWidth: "860px",
            }}
          >
            <h2 style={{ margin: "0 0 8px 0", fontSize: "17px", fontWeight: 700, color: "white" }}>
              Match Two Pairs (4-Person Double Date)
            </h2>
            <p style={{ margin: "0 0 20px 0", fontSize: "13px", color: "#9ca3af" }}>
              Select two 2-person Deuces teams to connect them in a 4-person double date match.
            </p>

            {loadingPairs ? (
              <div style={{ display: "flex", alignItems: "center", gap: "12px", color: "#9ca3af" }}>
                <CircleLoader color="#38bdf8" size={24} />
                <span>Loading available pairs…</span>
              </div>
            ) : pairs.length < 2 ? (
              <div style={{ color: "#f59e0b", fontSize: "14px" }}>
                ⚠️ You need at least 2 registered Deuces pairs to create a Double Date match. Form pairs in the "Registered Deuces Pairs" tab first.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "20px",
                    marginBottom: "24px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <label style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 500 }}>
                      Deuces Pair 1 (2 Users)
                    </label>
                    <Select
                      options={pairs.filter((p) => p.value !== selectPair2?.value)}
                      value={selectPair1}
                      onChange={setSelectPair1}
                      placeholder="Select first pair…"
                      styles={selectStyles}
                      components={{ Option: PairOption, SingleValue: PairSingleValue }}
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "28px",
                      alignSelf: "flex-end",
                      paddingBottom: "2px",
                    }}
                  >
                    🎉
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <label style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 500 }}>
                      Deuces Pair 2 (2 Users)
                    </label>
                    <Select
                      options={pairs.filter((p) => p.value !== selectPair1?.value)}
                      value={selectPair2}
                      onChange={setSelectPair2}
                      placeholder="Select second pair…"
                      styles={selectStyles}
                      components={{ Option: PairOption, SingleValue: PairSingleValue }}
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>

                {selectPair1 && selectPair2 && (
                  <div
                    style={{
                      backgroundColor: "#374151",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-around",
                      gap: "16px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "12px", color: "#38bdf8", fontWeight: 700, marginBottom: "4px" }}>
                        PAIR 1
                      </div>
                      <div style={{ fontWeight: 600, fontSize: "15px" }}>
                        {selectPair1.user1Name} &amp; {selectPair1.user2Name}
                      </div>
                    </div>
                    <div style={{ fontSize: "28px" }}>⚡ DOUBLE DATE ⚡</div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "12px", color: "#38bdf8", fontWeight: 700, marginBottom: "4px" }}>
                        PAIR 2
                      </div>
                      <div style={{ fontWeight: 600, fontSize: "15px" }}>
                        {selectPair2.user1Name} &amp; {selectPair2.user2Name}
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleCreateDoubleDate}
                  disabled={submittingDoubleDate || !selectPair1 || !selectPair2}
                  style={{
                    padding: "12px 32px",
                    backgroundColor:
                      submittingDoubleDate || !selectPair1 || !selectPair2 ? "#4b5563" : "#38bdf8",
                    color: submittingDoubleDate || !selectPair1 || !selectPair2 ? "#d1d5db" : "#0f172a",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: 700,
                    cursor: submittingDoubleDate || !selectPair1 || !selectPair2 ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "background-color 0.2s",
                  }}
                >
                  {submittingDoubleDate ? (
                    <>
                      <CircleLoader color="#0f172a" size={16} />
                      Connecting 4 Users…
                    </>
                  ) : (
                    "Create 4-Person Double Date Match"
                  )}
                </button>
              </>
            )}
          </div>

          <div style={{ maxWidth: "1000px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "14px",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
                Double Date Matches (4 Users per Match)
                <span style={{ fontSize: "14px", fontWeight: 400, color: "#9ca3af", marginLeft: "10px" }}>
                  {doubleDates.length} total
                </span>
              </h2>
              <button
                onClick={() => fetchDoubleDates(pairs)}
                style={{
                  padding: "7px 16px",
                  borderRadius: "6px",
                  border: "1px solid #4b5563",
                  backgroundColor: "transparent",
                  color: "#d1d5db",
                  fontSize: "13px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontFamily: "inherit",
                }}
              >
                Refresh
              </button>
            </div>

            {loadingDoubleDates ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "180px" }}>
                <CircleLoader color="#38bdf8" size={40} />
              </div>
            ) : doubleDates.length === 0 ? (
              <div
                style={{
                  backgroundColor: "#1f2937",
                  borderRadius: "10px",
                  padding: "40px",
                  textAlign: "center",
                  color: "#6b7280",
                  fontSize: "15px",
                }}
              >
                No 4-person Double Date matches recorded yet.
              </div>
            ) : (
              <div
                style={{
                  overflowX: "auto",
                  backgroundColor: "#1f2937",
                  borderRadius: "12px",
                  border: "1px solid #374151",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "850px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #374151", backgroundColor: "#111827", textAlign: "left" }}>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500 }}>Pair Team 1 (2 Users)</th>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500 }}>Pair Team 2 (2 Users)</th>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500 }}>Status</th>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500 }}>Date</th>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500, textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDoubleDates.map((dd) => (
                      <tr
                        key={dd.id}
                        style={{ borderBottom: "1px solid #374151", transition: "background-color 0.2s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#374151")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            {dd.pairA ? (
                              <>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <Avatar src={dd.pairA.user1Image} alt={dd.pairA.user1Name} sx={{ width: 28, height: 28 }} />
                                  <Avatar src={dd.pairA.user2Image} alt={dd.pairA.user2Name} sx={{ width: 28, height: 28, marginLeft: "-8px", border: "2px solid #1f2937" }} />
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600 }}>{dd.pairA.user1Name} &amp; {dd.pairA.user2Name}</div>
                                  <div style={{ fontSize: "11px", color: "#9ca3af" }}>ID: {dd.fromPairId?.slice(0, 8)}...</div>
                                </div>
                              </>
                            ) : (
                              <div style={{ color: "#9ca3af", fontSize: "13px" }}>Pair ID: {dd.fromPairId?.slice(0, 10)}...</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            {dd.pairB ? (
                              <>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <Avatar src={dd.pairB.user1Image} alt={dd.pairB.user1Name} sx={{ width: 28, height: 28 }} />
                                  <Avatar src={dd.pairB.user2Image} alt={dd.pairB.user2Name} sx={{ width: 28, height: 28, marginLeft: "-8px", border: "2px solid #1f2937" }} />
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600 }}>{dd.pairB.user1Name} &amp; {dd.pairB.user2Name}</div>
                                  <div style={{ fontSize: "11px", color: "#9ca3af" }}>ID: {dd.toPairId?.slice(0, 8)}...</div>
                                </div>
                              </>
                            ) : (
                              <div style={{ color: "#9ca3af", fontSize: "13px" }}>Pair ID: {dd.toPairId?.slice(0, 10)}...</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <span
                            style={{
                              padding: "4px 10px",
                              borderRadius: "999px",
                              fontSize: "12px",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              backgroundColor:
                                dd.status === "accepted"
                                  ? "#065f46"
                                  : dd.status === "rejected"
                                    ? "#7f1d1d"
                                    : "#78350f",
                              color:
                                dd.status === "accepted"
                                  ? "#34d399"
                                  : dd.status === "rejected"
                                    ? "#f87171"
                                    : "#fbbf24",
                            }}
                          >
                            {dd.status}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: "#9ca3af" }}>
                          {dd.createdAt}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                          <button
                            onClick={() => handleDeleteDoubleDate(dd)}
                            disabled={deletingDoubleDateId === dd.id}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#ef4444",
                              fontSize: "13px",
                              fontWeight: 600,
                            }}
                          >
                            {deletingDoubleDateId === dd.id ? <CircleLoader size={16} color="#ef4444" /> : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalDoubleDatePages > 1 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
                <button
                  disabled={currentDoubleDatePage === 1}
                  onClick={() => setCurrentDoubleDatePage((p) => Math.max(1, p - 1))}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: currentDoubleDatePage === 1 ? "#374151" : "#38bdf8",
                    color: currentDoubleDatePage === 1 ? "#fff" : "#0f172a",
                    border: "none",
                    borderRadius: "6px",
                    cursor: currentDoubleDatePage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Previous
                </button>
                <span style={{ color: "#9ca3af", fontSize: "14px" }}>
                  Page {currentDoubleDatePage} of {totalDoubleDatePages}
                </span>
                <button
                  disabled={currentDoubleDatePage === totalDoubleDatePages}
                  onClick={() => setCurrentDoubleDatePage((p) => Math.min(totalDoubleDatePages, p + 1))}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: currentDoubleDatePage === totalDoubleDatePages ? "#374151" : "#38bdf8",
                    color: currentDoubleDatePage === totalDoubleDatePages ? "#fff" : "#0f172a",
                    border: "none",
                    borderRadius: "6px",
                    cursor: currentDoubleDatePage === totalDoubleDatePages ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "pairs" && (
        <>
          <div
            style={{
              backgroundColor: "#1f2937",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "32px",
              maxWidth: "860px",
            }}
          >
            <h2 style={{ margin: "0 0 8px 0", fontSize: "17px", fontWeight: 700, color: "white" }}>
              Form a 2-Person Deuces Team
            </h2>
            <p style={{ margin: "0 0 20px 0", fontSize: "13px", color: "#9ca3af" }}>
              Team up two friends together so they can browse other pairs for double dates.
            </p>

            {loadingUsers ? (
              <div style={{ display: "flex", alignItems: "center", gap: "12px", color: "#9ca3af" }}>
                <CircleLoader color="#a855f7" size={24} />
                <span>Loading users…</span>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: "20px",
                    marginBottom: "24px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <label style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 500 }}>
                      Partner 1
                    </label>
                    <Select
                      options={users.filter((u) => u.value !== pairUser2?.value)}
                      value={pairUser1}
                      onChange={setPairUser1}
                      placeholder="Search user…"
                      styles={selectStyles}
                      components={{ Option: UserOption, SingleValue: UserSingleValue }}
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "28px",
                      alignSelf: "flex-end",
                      paddingBottom: "2px",
                    }}
                  >
                    🤝
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <label style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 500 }}>
                      Partner 2
                    </label>
                    <Select
                      options={users.filter((u) => u.value !== pairUser1?.value)}
                      value={pairUser2}
                      onChange={setPairUser2}
                      placeholder="Search user…"
                      styles={selectStyles}
                      components={{ Option: UserOption, SingleValue: UserSingleValue }}
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>

                {pairUser1 && pairUser2 && (
                  <div
                    style={{
                      backgroundColor: "#374151",
                      borderRadius: "10px",
                      padding: "16px",
                      marginBottom: "20px",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Avatar
                        src={pairUser1.imageUrl}
                        alt={pairUser1.label}
                        sx={{ width: 44, height: 44 }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "14px" }}>{pairUser1.label}</div>
                        <div style={{ fontSize: "12px", color: "#9ca3af" }}>{pairUser1.email}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: "24px", padding: "0 8px" }}>🤝</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Avatar
                        src={pairUser2.imageUrl}
                        alt={pairUser2.label}
                        sx={{ width: 44, height: 44 }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "14px" }}>{pairUser2.label}</div>
                        <div style={{ fontSize: "12px", color: "#9ca3af" }}>{pairUser2.email}</div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleCreatePair}
                  disabled={submittingPair || !pairUser1 || !pairUser2}
                  style={{
                    padding: "12px 32px",
                    backgroundColor:
                      submittingPair || !pairUser1 || !pairUser2 ? "#4b5563" : "#a855f7",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "15px",
                    fontWeight: 700,
                    cursor: submittingPair || !pairUser1 || !pairUser2 ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "background-color 0.2s",
                  }}
                >
                  {submittingPair ? (
                    <>
                      <CircleLoader color="white" size={16} />
                      Creating Pair…
                    </>
                  ) : (
                    "Form 2-Person Deuces Pair"
                  )}
                </button>
              </>
            )}
          </div>
          <div style={{ maxWidth: "960px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "14px",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
                Registered Deuces Pairs (2 Users per Pair)
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#9ca3af",
                    marginLeft: "10px",
                  }}
                >
                  {pairs.length} registered pair{pairs.length !== 1 ? "s" : ""}
                </span>
              </h2>
              <button
                onClick={() => fetchPairs(users)}
                style={{
                  padding: "7px 16px",
                  borderRadius: "6px",
                  border: "1px solid #4b5563",
                  backgroundColor: "transparent",
                  color: "#d1d5db",
                  fontSize: "13px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontFamily: "inherit",
                }}
              >
                Refresh
              </button>
            </div>

            {loadingPairs ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "180px" }}>
                <CircleLoader color="#a855f7" size={40} />
              </div>
            ) : pairs.length === 0 ? (
              <div
                style={{
                  backgroundColor: "#1f2937",
                  borderRadius: "10px",
                  padding: "40px",
                  textAlign: "center",
                  color: "#6b7280",
                  fontSize: "15px",
                }}
              >
                No Deuces pairs formed on the application yet.
              </div>
            ) : (
              <div
                style={{
                  overflowX: "auto",
                  backgroundColor: "#1f2937",
                  borderRadius: "12px",
                  border: "1px solid #374151",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #374151", backgroundColor: "#111827", textAlign: "left" }}>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500 }}>Partner 1</th>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500 }}>Partner 2</th>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500 }}>Pair Created At</th>
                      <th style={{ padding: "16px", color: "#9ca3af", fontWeight: 500, textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPairs.map((pair) => (
                      <tr
                        key={pair.id}
                        style={{ borderBottom: "1px solid #374151", transition: "background-color 0.2s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#374151")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Avatar src={pair.user1Image} alt={pair.user1Name} sx={{ width: 36, height: 36 }} />
                            <div>
                              <div style={{ fontWeight: 600 }}>{pair.user1Name}</div>
                              <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                                {pair.user1Email || `${pair.user1Id.slice(0, 10)}...`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Avatar src={pair.user2Image} alt={pair.user2Name} sx={{ width: 36, height: 36 }} />
                            <div>
                              <div style={{ fontWeight: 600 }}>{pair.user2Name}</div>
                              <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                                {pair.user2Email || `${pair.user2Id.slice(0, 10)}...`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "13px", color: "#9ca3af" }}>
                          {pair.createdAt}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                          <button
                            onClick={() => handleDeletePair(pair)}
                            disabled={deletingPairId === pair.id}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#ef4444",
                              fontSize: "13px",
                              fontWeight: 600,
                            }}
                          >
                            {deletingPairId === pair.id ? <CircleLoader size={16} color="#ef4444" /> : "Unpair / Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPairPages > 1 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
                <button
                  disabled={currentPairPage === 1}
                  onClick={() => setCurrentPairPage((p) => Math.max(1, p - 1))}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: currentPairPage === 1 ? "#374151" : "#a855f7",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: currentPairPage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Previous
                </button>
                <span style={{ color: "#9ca3af", fontSize: "14px" }}>
                  Page {currentPairPage} of {totalPairPages}
                </span>
                <button
                  disabled={currentPairPage === totalPairPages}
                  onClick={() => setCurrentPairPage((p) => Math.min(totalPairPages, p + 1))}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: currentPairPage === totalPairPages ? "#374151" : "#a855f7",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: currentPairPage === totalPairPages ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Matches;
